import { createContext, useContext, useState, useEffect } from 'react';

type TranslationContextType = {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
  loading: boolean;
};

const TranslationContext = createContext<TranslationContextType>({
  language: 'ES',
  setLanguage: () => {},
  t: (key) => key,
  loading: true,
});

export function TranslationProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState('ES');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTranslations() {
      try {
        const response = await fetch(`./languages/${language.toUpperCase()}.json`);
        const data = await response.json();
        setTranslations(data);
      } catch (error) {
        console.error("Error loading translations:", error);
      } finally {
        setLoading(false);
      }
    }
    
    loadTranslations();
  }, [language]);

  const t = (key: string): string => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return key;
    }
    
    // Asegurar que siempre devuelva string
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  useEffect(() => {
    localStorage.setItem('userLanguage', language);
  }, [language]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('userLanguage');
    const browserLanguage = navigator.language.split('-')[0].toUpperCase();
    
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else if (['ES', 'EN', 'FR'].includes(browserLanguage)) {
      setLanguage(browserLanguage);
    }
  }, []);

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t, loading }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}